import { Parameter, DataType, UnifiedSchema } from '../types/schema';
import { PlaybookTransformItem, PlaybookMapping, PlaybookSpreading, PlaybookExpression, PlaybookEnrichment, PlaybookTransformType } from '../types/playbook'; // Import PlaybookTransformType
import { buildParameterTree, toFriendlyName } from './schemaUtils';
import { SchemaDefinition } from '../types/contract';

export interface SchemaComparisonResult {
  missing: Array<{ path: string; type: string }>;
  extra: Array<{ path: string; type: string }>;
  typeMismatches: Array<{ path: string; expected: string; actual: string }>;
}

export interface SchemaProperty {
  path: string;
  dataType: string;
  description?: string;
  status: 'mapped' | 'missing' | 'extra' | 'mismatch' | 'incomplete';
  source?: string; // Which transform or event created this (e.g., "Mapping: event -> event")
  sourceTransformType?: PlaybookTransformType; // Add sourceTransformType
  sourceTransformId?: string; // The actual ID of the transform
  sourceEventId?: string; // Add sourceEventId to SchemaProperty
  sourceInputPath?: string; // Input path from the source transform
  sourceOutputPath?: string; // Output path from the source transform
  sourceAppliesToScope?: 'global' | 'eventSpecific'; // Scope from the source transform
  typeMismatch?: {
    expected: string;
    actual: string;
  };
  isPII?: boolean; // Add isPII to SchemaProperty
  isRequired?: boolean; // Add isRequired to SchemaProperty
  regexFormula?: string; // Add regexFormula to SchemaProperty
}

/**
 * Helper function to check if a transform is properly configured.
 * This function is duplicated from PlaybookSchemaPreviewPanel.tsx to be reusable.
 */
export const isTransformProperlyConfigured = (transform: PlaybookTransformItem): boolean => {
  switch (transform.type) {
    case 'mapping':
      const mappingContent = transform.content as PlaybookMapping;
      return !!(mappingContent.inputKey && mappingContent.outputKey);
    case 'enrichment':
      const enrichmentContent = transform.content as PlaybookEnrichment;
      return !!(enrichmentContent.outputKey);
    case 'expression':
      const expressionContent = transform.content as PlaybookExpression;
      return !!(expressionContent.declaredOutputProperties && expressionContent.declaredOutputProperties.length > 0);
    case 'spreading':
      const spreadingContent = transform.content as PlaybookSpreading;
      return !!(spreadingContent.inputKey && spreadingContent.inferredOutputProperties && spreadingContent.inferredOutputProperties.length > 0);
    case 'filter':
      // Filters don't contribute to schema properties, so they're always "configured" for schema purposes
      return true;
    default:
      return false;
  }
};

/**
 * Converts SchemaProperty[] to Parameter[] for use with tree components
 * Uses stable IDs based on fullPath to avoid React key duplication warnings
 */
export const convertToParameters = (properties: SchemaProperty[]): Parameter[] => {
  const parameters: Parameter[] = [];
  const paramMap = new Map<string, Parameter>();
  
  properties.forEach(prop => {
    const pathParts = prop.path.split('.');
    const codeName = pathParts[pathParts.length - 1];
    
    // Generate stable ID based on fullPath hash to avoid regeneration
    const stableId = generateStableId(prop.path);
    
    // Create parameter
    const param: Parameter = {
      id: stableId,
      path: prop.path,
      fullPath: prop.path,
      codeName: codeName,
      friendlyName: toFriendlyName(codeName),
      description: prop.description || '',
      dataTypes: [prop.dataType as DataType],
      priorities: prop.isRequired ? ['Required'] : ['Standard'], // Set priority based on isRequired
      regexFormula: prop.regexFormula || '', // Pass regexFormula
      llmInstructions: undefined, // Not derived here
      isPii: prop.isPII, // Pass isPII
      scope: 'global',
      children: [],
      isExpanded: true,
      level: pathParts.length - 1,
      parentPaths: pathParts.slice(0, -1),
      isLastChild: false
    };
    
    // Add status metadata for color coding
    (param as any).status = prop.status;
    (param as any).source = prop.source;
    (param as any).sourceTransformType = prop.sourceTransformType; // Pass sourceTransformType
    (param as any).sourceTransformId = prop.sourceTransformId; // Pass sourceTransformId
    (param as any).sourceEventId = prop.sourceEventId; // Pass sourceEventId
    (param as any).sourceInputPath = prop.sourceInputPath; // Pass sourceInputPath
    (param as any).sourceOutputPath = prop.sourceOutputPath; // Pass sourceOutputPath
    (param as any).sourceAppliesToScope = prop.sourceAppliesToScope; // Pass sourceAppliesToScope
    (param as any).typeMismatch = prop.typeMismatch;
    
    parameters.push(param);
    paramMap.set(prop.path, param);
  });
  
  // Build parent-child relationships
  return buildParameterTree(parameters);
};

/**
 * Generates a stable ID based on the path to avoid React key duplication warnings
 */
function generateStableId(path: string): string {
  // Simple hash function to create stable IDs
  let hash = 0;
  for (let i = 0; i < path.length; i++) {
    const char = path.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash; // Convert to 32-bit integer
  }
  return `schema-param-${Math.abs(hash)}`;
}

/**
 * Enhanced schema comparison that provides more detailed feedback
 */
export const enhancedCompareSchemas = (
  derived: SchemaProperty[], 
  target: SchemaDefinition
): SchemaComparisonResult => {
  // Parse target schema into a more usable format
  const targetProperties = parseTargetSchema(target);
  
  const derivedPaths = new Set(derived.map(p => p.path));
  const targetPaths = new Set(targetProperties.map(p => p.path));
  
  // Find missing properties (in target but not in derived)
  const missing = targetProperties
    .filter(p => !derivedPaths.has(p.path))
    .map(p => ({ 
      path: p.path, 
      type: p.dataType || 'unknown'
    }));

  // Find extra properties (in derived but not in target)
  const extra = derived
    .filter(p => !targetPaths.has(p.path))
    .map(p => ({ 
      path: p.path, 
      type: p.dataType
    }));

  // Find type mismatches
  const typeMismatches = derived
    .filter(p => {
      const targetProp = targetProperties.find(tp => tp.path === p.path);
      return targetProp && targetProp.dataType !== p.dataType;
    })
    .map(p => {
      const targetProp = targetProperties.find(tp => tp.path === p.path)!;
      return {
        path: p.path,
        expected: targetProp.dataType,
        actual: p.dataType
      };
    });

  return { missing, extra, typeMismatches };
};

/**
 * Parse target schema definition into a simplified format with nested property support
 */
const parseTargetSchema = (schema: SchemaDefinition): Array<{path: string, dataType: string, description?: string}> => {
  const properties: Array<{path: string, dataType: string, description?: string}> = [];
  
  // Handle different schema formats
  if ((schema as any).parameters && Array.isArray((schema as any).parameters)) {
    // Handle UnifiedSchema format with parameters array
    (schema as any).parameters.forEach((param: any) => {
      properties.push({
        path: param.fullPath || param.path,
        dataType: param.dataTypes?.[0] || 'string',
        description: param.description
      });
    });
  } else if (schema.properties && Array.isArray(schema.properties)) {
    // Handle legacy format with properties array
    schema.properties.forEach(prop => {
      properties.push({
        path: prop.path || prop.name || '',
        dataType: prop.type || 'string',
        description: (prop as any).description
      });
    });
  } else if (schema.properties && typeof schema.properties === 'object') {
    // Handle JSON Schema format with nested properties object
    extractNestedSchemaProperties(schema.properties, '', properties);
  }
  
  return properties;
};

/**
 * Recursively extract properties from nested schema objects
 */
function extractNestedSchemaProperties(
  schemaObj: any, 
  parentPath: string, 
  properties: Array<{path: string, dataType: string, description?: string}>
): void {
  if (!schemaObj || typeof schemaObj !== 'object') return;
  
  for (const [key, value] of Object.entries(schemaObj)) {
    if (!value || typeof value !== 'object') continue;
    
    const currentPath = parentPath ? `${parentPath}.${key}` : key;
    const schemaValue = value as any;
    
    // Determine data type
    let dataType = 'string';
    if (schemaValue.type) {
      dataType = schemaValue.type;
    } else if (schemaValue.properties) {
      dataType = 'object';
    } else if (schemaValue.items) {
      dataType = 'array';
    }
    
    // Add current property
    properties.push({
      path: currentPath,
      dataType: dataType,
      description: schemaValue.description || schemaValue.title
    });
    
    // Recursively process nested properties
    if (schemaValue.properties && typeof schemaValue.properties === 'object') {
      extractNestedSchemaProperties(schemaValue.properties, currentPath, properties);
    }
    
    // Handle array items with properties
    if (schemaValue.items && schemaValue.items.properties && typeof schemaValue.items.properties === 'object') {
      extractNestedSchemaProperties(schemaValue.items.properties, currentPath, properties);
    }
  }
}

/**
 * Update schema properties with comparison results
 */
export const updateSchemaPropertiesWithComparison = (
  properties: SchemaProperty[],
  comparison: SchemaComparisonResult
): SchemaProperty[] => {
  // Create a map for quick lookup
  const propertyMap = new Map<string, SchemaProperty>();
  properties.forEach(prop => {
    propertyMap.set(prop.path, prop);
  });
  
  // Update existing properties with type mismatch info
  comparison.typeMismatches.forEach(mismatch => {
    const prop = propertyMap.get(mismatch.path);
    if (prop) {
      prop.status = 'mismatch';
      prop.typeMismatch = {
        expected: mismatch.expected,
        actual: mismatch.actual
      };
    }
  });
  
  // Start with existing properties
  const updatedProperties = [...properties];
  
  // Handle missing properties - these should appear as "missing" in the schema
  comparison.missing.forEach(missing => {
    const existingProp = propertyMap.get(missing.path);
    if (existingProp) {
      // If the property exists but is missing from transforms, mark it as missing
      // This handles the case where a transform was deleted
      existingProp.status = 'missing';
      existingProp.description = 'Required by target schema but not mapped by any transform';
      existingProp.source = undefined;
      existingProp.sourceTransformType = undefined;
      existingProp.sourceTransformId = undefined;
      existingProp.sourceEventId = undefined;
      existingProp.sourceInputPath = undefined;
      existingProp.sourceOutputPath = undefined;
      existingProp.sourceAppliesToScope = undefined;
    } else {
      // Add new missing property
      updatedProperties.push({
        path: missing.path,
        dataType: missing.type,
        status: 'missing',
        description: 'Required by target schema but not mapped by any transform'
      });
    }
  });
  
  // Mark extra properties and add them if not already present
  comparison.extra.forEach(extra => {
    const prop = propertyMap.get(extra.path);
    if (prop) {
      prop.status = 'extra';
    } else {
      // If the extra property is not already in the derived properties, add it
      updatedProperties.push({
        path: extra.path,
        dataType: extra.type,
        status: 'extra',
        description: 'Generated by transforms but not expected in target schema'
      });
    }
  });
  
  return updatedProperties;
};

/**
 * Calculates mapped and required mapped field counts for a given schema based on playbook transforms.
 * This function is designed to work for both input and output schemas.
 *
 * For input schema: counts parameters that are used as input in transforms.
 * For output schema: counts parameters that are generated as output by transforms and exist in the target schema.
 */
export function getMappedFieldCounts(
  schema: UnifiedSchema,
  uiTransforms: PlaybookTransformItem[],
  isInputSchema: boolean
): { mappedCount: number; requiredMappedCount: number } {
  const allSchemaPaths = new Set(schema.parameters.map(p => p.fullPath));
  const requiredSchemaPaths = new Set(
    schema.parameters.filter(p => p.priorities.includes('Required')).map(p => p.fullPath)
  );

  const mappedPaths = new Set<string>();

  uiTransforms.forEach(transform => {
    // Only count as mapped if the transform is properly configured
    if (!isTransformProperlyConfigured(transform)) {
      return;
    }

    if (isInputSchema) {
      // For input schema, we look at input keys and referenced paths in expressions/filters
      if (transform.type === 'mapping') {
        const content = transform.content as PlaybookMapping;
        if (content.inputKey && allSchemaPaths.has(content.inputKey)) {
          mappedPaths.add(content.inputKey);
        }
      } else if (transform.type === 'spreading') {
        const content = transform.content as PlaybookSpreading;
        if (content.inputKey && allSchemaPaths.has(content.inputKey)) {
          mappedPaths.add(content.inputKey);
        }
      } else if (transform.type === 'expression') {
        // A full solution would parse the expression body to find all referenced input parameters.
        // For now, we'll assume explicit declarations or that input references are handled implicitly by other transforms.
        // If expression references input fields, they should be added to mappedPaths.
      } else if (transform.type === 'filter') {
        // Filters also reference input fields. Parsing rules can be complex.
        // For simplicity, we assume filter rules are not directly contributing to "mapped" input fields
        // in the same way as mappings/spreadings for progress bar purposes,
        // or that the relevant fields are already covered by other transform types.
      }
    } else {
      // For output schema, we look at output keys
      if (transform.outputPath && allSchemaPaths.has(transform.outputPath)) {
        mappedPaths.add(transform.outputPath);
      }
      if (transform.type === 'expression') {
        const content = transform.content as PlaybookExpression;
        content.declaredOutputProperties?.forEach(prop => {
          if (allSchemaPaths.has(prop.path)) {
            mappedPaths.add(prop.path);
          }
        });
      }
      if (transform.type === 'spreading') {
        const content = transform.content as PlaybookSpreading;
        content.inferredOutputProperties?.forEach(prop => {
          if (allSchemaPaths.has(prop.path)) {
            mappedPaths.add(prop.path);
          }
        });
      }
    }
  });

  const mappedCount = mappedPaths.size;
  const requiredMappedCount = Array.from(mappedPaths).filter(path => requiredSchemaPaths.has(path)).length;

  return { mappedCount, requiredMappedCount };
}

/**
 * Helper to get total and required counts for a schema.
 */
export function getSchemaFieldCounts(schema: UnifiedSchema): { totalCount: number; requiredTotalCount: number } {
  const totalCount = schema.parameters.length;
  const requiredTotalCount = schema.parameters.filter(p => p.priorities.includes('Required')).length;
  return { totalCount, requiredTotalCount };
}

/**
 * Enhanced function to derive schema properties with mapping status
 * Supports nested properties and provides better transform matching
 * Filters out generic event properties that shouldn't appear in schema preview
 */
export function deriveSchemaPropertiesWithMappingStatus(
  schema: any,
  transforms: PlaybookTransformItem[],
  baselineSchema: 'input' | 'output'
): SchemaProperty[] {
  if (!schema || !schema.parameters) return [];
  
  const properties: SchemaProperty[] = [];
  
  // Define generic event properties that should be filtered out from schema preview
  const genericEventProperties = new Set([
    'Event Name',
    'event_name',
    'Event ID',
    'event_id',
    'Event Type',
    'event_type',
    'Timestamp',
    'timestamp',
    'User ID',
    'user_id',
    'Session ID',
    'session_id'
  ]);
  
  schema.parameters.forEach((param: any) => {
    const paramPath = param.fullPath || param.path;
    const paramCodeName = param.codeName || paramPath.split('.').pop();
    
    // Skip generic event properties that are automatically added to all events
    if (genericEventProperties.has(paramPath) || genericEventProperties.has(paramCodeName)) {
      return;
    }
    
    // Check if this parameter is mapped by any transform
    const mappingTransform = findMappingTransform(param, transforms, baselineSchema);
    
    const status = mappingTransform ? 'mapped' : 'missing';
    const source = mappingTransform 
      ? `${mappingTransform.type}: ${mappingTransform.name || 'Unnamed transform'}`
      : undefined;

    properties.push({
      path: paramPath,
      dataType: param.dataTypes?.[0] || 'unknown',
      description: param.description || '',
      status,
      source,
    });
  });

  return properties;
}

/**
 * Helper function to find if a parameter has a corresponding transform
 */
function findMappingTransform(
  param: any,
  transforms: PlaybookTransformItem[],
  baselineSchema: 'input' | 'output'
): PlaybookTransformItem | undefined {
  const paramPath = param.fullPath || param.path;
  
  return transforms.find(transform => {
    if (transform.type === 'mapping') {
      const mappingContent = transform.content as PlaybookMapping;
      return baselineSchema === 'input' 
        ? mappingContent.inputKey === paramPath
        : mappingContent.outputKey === paramPath;
    }
    
    if (transform.type === 'enrichment') {
      const enrichmentContent = transform.content as PlaybookEnrichment;
      return baselineSchema === 'output' && 
        enrichmentContent.outputKey === paramPath;
    }
    
    if (transform.type === 'expression') {
      const expressionContent = transform.content as PlaybookExpression;
      if (baselineSchema === 'output' && expressionContent.declaredOutputProperties) {
        return expressionContent.declaredOutputProperties.some(prop => prop.path === paramPath);
      }
    }
    
    if (transform.type === 'spreading') {
      const spreadingContent = transform.content as PlaybookSpreading;
      if (baselineSchema === 'input') {
        return spreadingContent.inputKey === paramPath;
      } else if (baselineSchema === 'output' && spreadingContent.inferredOutputProperties) {
        return spreadingContent.inferredOutputProperties.some(prop => prop.path === paramPath);
      }
    }
    
    return false;
  });
}
